import React, { useState, useEffect } from 'react';
import { ingestionAPI, judgeAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function EpaperUpload() {
  const [file, setFile] = useState(null);
  const [articleDate, setArticleDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [epaperDates, setEpaperDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [dateArticles, setDateArticles] = useState([]);
  const [loadingDateArticles, setLoadingDateArticles] = useState(false);

  useEffect(() => {
    loadUploadHistory();
    loadEpaperDates();
  }, []);

  const loadEpaperDates = async () => {
    setLoadingDates(true);
    try {
      const response = await judgeAPI.getEpaperDates(7);
      const dates = response.data?.dates || [];
      setEpaperDates(dates);
    } catch (error) {
      console.error('Failed to load e-paper dates:', error);
      toast.error('Failed to load e-paper date cards');
    } finally {
      setLoadingDates(false);
    }
  };

  const loadArticlesByDate = async (date) => {
    if (!date) return;
    setLoadingDateArticles(true);
    try {
      const response = await judgeAPI.getEpaperArticlesByDate(date, 0, 200);
      setDateArticles(response.data?.data || []);
      setSelectedDate(date);
    } catch (error) {
      console.error('Failed to load e-paper articles by date:', error);
      toast.error('Failed to load articles for selected date');
    } finally {
      setLoadingDateArticles(false);
    }
  };

  const handleDateSearch = async () => {
    if (!searchDate) {
      toast.error('Please select a date to search');
      return;
    }
    await loadArticlesByDate(searchDate);
  };

  const loadUploadHistory = async () => {
    setLoadingHistory(true);
    try {
      // Try to fetch from API endpoint (if available)
      try {
        const response = await ingestionAPI.getEpaperUploadHistory();
        setUploadHistory(response.data || []);
      } catch (apiError) {
        // If endpoint doesn't exist, derive from scored articles with epaper source
        const response = await judgeAPI.getScoredArticles();
        const articles = response.data?.data || [];
        
        // Group epaper articles by date and source to estimate uploads
        const epaperArticles = articles.filter(a => 
          a.source_name && (a.source_name.includes('E-paper') || a.source_name.includes('PDF'))
        );
        
        const uploadGroups = {};
        epaperArticles.forEach(article => {
          const timelineDate = article.published_at || article.scored_at;
          if (timelineDate) {
            const date = new Date(timelineDate);
            const dateKey = date.toISOString().split('T')[0] + '_' + (article.source_name || 'unknown');
            
            if (!uploadGroups[dateKey]) {
              uploadGroups[dateKey] = {
                date: date,
                fileName: `${article.source_name}_${date.toISOString().split('T')[0]}.pdf`,
                articlesExtracted: 0,
                status: 'success',
                size: 'N/A'
              };
            }
            uploadGroups[dateKey].articlesExtracted++;
          }
        });
        
        const history = Object.values(uploadGroups)
          .sort((a, b) => b.date - a.date)
          .slice(0, 10);
        
        setUploadHistory(history);
      }
    } catch (error) {
      console.error('Failed to load upload history:', error);
      toast.error('Failed to load upload history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (!articleDate) {
      toast.error('Please select article date');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    const toastId = toast.loading('Uploading e-paper...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('article_date', articleDate);

      const response = await ingestionAPI.uploadEpaper(formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setResult(response.data);
      toast.success(`E-paper uploaded! ${response.data.articles_extracted || 0} articles extracted`, { id: toastId });
      setFile(null);
      setArticleDate('');
      // Reset file input
      document.getElementById('file-input').value = '';
      // Reload history after successful upload
      await loadUploadHistory();
      await loadEpaperDates();
      if (response.data?.article_date) {
        await loadArticlesByDate(response.data.article_date);
      }
    } catch (error) {
      toast.error(`Upload failed: ${error.response?.data?.message || error.message}`, { id: toastId });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Modern Header */}
      <div className="relative bg-gradient-to-r from-purple-600 via-indigo-700 to-blue-800 rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="relative">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-1">E-paper Upload 📰</h2>
              <p className="text-white/90">
                Upload newspaper e-paper PDFs for article extraction
              </p>
            </div>
          </div>
          {/* Upload Area */}
          <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 border-dashed rounded-2xl p-8">
            <div className="text-center">
              <svg
                className="mx-auto h-16 w-16 text-white/80"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="mt-4 flex text-sm leading-6 text-white justify-center">
                <label
                  htmlFor="file-input"
                  className="relative cursor-pointer rounded-xl bg-white/20 backdrop-blur-sm px-4 py-2 font-bold text-white hover:bg-white/30 transition-all"
                >
                  <span>📤 Upload a file</span>
                  <input
                    id="file-input"
                    name="file-input"
                    type="file"
                    className="sr-only"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </label>
                <p className="pl-2 text-white/90">or drag and drop</p>
              </div>
              <p className="text-sm text-white/80 mt-2">PDF up to 50MB</p>
            </div>

            {/* Date Picker */}
            <div className="mt-6 flex items-center justify-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/30">
                <label className="flex items-center text-white">
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold mr-3">Article Date:</span>
                  <input
                    type="date"
                    value={articleDate}
                    onChange={(e) => setArticleDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    disabled={uploading}
                    className="bg-white/30 text-white border border-white/40 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50"
                    required
                  />
                </label>
              </div>
            </div>

            {file && (
              <div className="mt-6 flex items-center justify-center">
                <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/30">
                  <svg className="h-6 w-6 text-white mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="text-white">
                    <div className="font-semibold">{file.name}</div>
                    <div className="text-sm text-white/80">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                  {!uploading && (
                    <button
                      onClick={() => {
                        setFile(null);
                        document.getElementById('file-input').value = '';
                      }}
                      className="ml-4 text-white hover:text-red-200 transition-colors"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/30">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-bold text-white">Uploading...</span>
                <span className="text-sm font-bold text-white">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-400 to-emerald-500 h-3 rounded-full transition-all duration-300 shadow-lg"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload Button - Only show when file is selected */}
          {file && !uploading && (
            <div className="flex justify-center">
              <button
                onClick={handleUpload}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold shadow-lg transition-all hover:scale-105 border-2 border-white/30"
              >
                🚀 Upload E-paper
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-white/20 backdrop-blur-md border-2 border-green-300/50 rounded-2xl p-6">
              <div className="flex items-start">
                <div className="bg-green-500 rounded-xl p-3 mr-4">
                  <svg className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 text-white">
                  <h3 className="text-lg font-bold mb-2">✅ Upload Successful!</h3>
                  <p className="font-semibold">📰 Articles extracted: {result.articles_extracted || 0}</p>
                  <p className="text-sm text-white/90 mt-1">Published to Kafka topic: <code className="bg-white/20 px-2 py-1 rounded">news.raw.ingested</code></p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Date Cards and Search */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 mb-8">
        <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 px-6 py-4 border-b-2 border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">E-paper Articles By Date</h3>
          <p className="text-sm text-gray-600 mt-1">Click a recent date card or search by date to load all e-paper articles.</p>
        </div>

        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="date"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
              className="w-full md:w-72 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400"
              max={new Date().toISOString().split('T')[0]}
            />
            <button
              onClick={handleDateSearch}
              disabled={loadingDateArticles}
              className="px-5 py-2 rounded-lg bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-50"
            >
              Search Date
            </button>
          </div>
        </div>

        <div className="p-6">
          {loadingDates ? (
            <div className="text-gray-600">Loading date cards...</div>
          ) : epaperDates.length === 0 ? (
            <div className="text-gray-600">No e-paper dates found yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {epaperDates.map((dateCard) => (
                <button
                  key={dateCard.date}
                  onClick={() => loadArticlesByDate(dateCard.date)}
                  className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${selectedDate === dateCard.date ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white'}`}
                >
                  <div className="text-sm text-gray-500">Publication Date</div>
                  <div className="text-lg font-bold text-gray-900">{dateCard.label || dateCard.date}</div>
                  <div className="text-sm text-gray-700 mt-2">{dateCard.count} articles</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Articles for selected date */}
      {(selectedDate || loadingDateArticles) && (
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 mb-8">
          <div className="bg-gradient-to-r from-sky-50 via-cyan-50 to-teal-50 px-6 py-4 border-b-2 border-gray-200">
            <h3 className="text-xl font-bold text-gray-900">
              Articles for {selectedDate || 'selected date'}
            </h3>
          </div>
          <div className="p-6">
            {loadingDateArticles ? (
              <div className="text-gray-600">Loading articles...</div>
            ) : dateArticles.length === 0 ? (
              <div className="text-gray-600">No articles found for this date.</div>
            ) : (
              <div className="space-y-4">
                {dateArticles.map((article) => (
                  <div key={article._id} className="border border-gray-200 rounded-xl p-4 hover:shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-900">{article.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {article.source_name || 'E-paper Upload'}
                      {article.max_score !== undefined && article.max_score !== null ? ` | Score: ${article.max_score}` : ''}
                    </p>
                    {article.summary && (
                      <p className="text-sm text-gray-700 mt-2">{article.summary}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload History Table */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-gray-50 via-purple-50 to-indigo-50 px-6 py-4 border-b-2 border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <svg className="h-6 w-6 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            📂 Upload History
          </h3>
        </div>
        <div className="overflow-x-auto">
          {loadingHistory ? (
            <div className="flex justify-center items-center p-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-600"></div>
                <div className="absolute top-0 left-0 h-12 w-12 rounded-full bg-purple-100"></div>
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">File Name</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Articles Extracted</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {uploadHistory.map((upload, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{upload.date.toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{upload.date.toLocaleTimeString()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-900">{upload.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{upload.size}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{upload.articlesExtracted}</div>
                      <div className="text-xs text-gray-500">articles</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {upload.status === 'success' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Success
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
